import { token } from '@atlaskit/tokens';
import { css } from '@compiled/react';

interface CalendarDateProps {
  /**
   * Pre-formatted month string (e.g., "NOV", "DEC").
   * If provided, this will be used instead of auto-formatting from date.
   */
  monthString?: string;
  /**
   * Pre-formatted day string (e.g., "15", "1").
   * If provided, this will be used instead of auto-formatting from date.
   */
  dayString?: string;
  /**
   * The date to display. Can be a Date object or an ISO date string.
   * Used as a fallback if monthString and dayString are not provided.
   */
  date?: Date | string;
  /**
   * Whether the date is in the past (will change the styles of the component).
   */
  isPast?: boolean;
}

/**
 * A calendar-style date display component that shows the month abbreviation
 * and day number in a visually distinct calendar icon format.
 *
 * Prefers pre-formatted strings (monthString/dayString) but will auto-format
 * from a date if those are not provided.
 *
 * @example
 * // With pre-formatted strings (preferred)
 * <CalendarDate monthString="NOV" dayString="15" />
 *
 * @example
 * // With Date object (fallback)
 * <CalendarDate date={new Date('2025-11-15')} />
 *
 * @example
 * // With ISO string (fallback)
 * <CalendarDate date="2025-11-15T10:00:00Z" />
 */
export const CalendarDate = ({ monthString, dayString, date, isPast }: CalendarDateProps) => {
  let month: string;
  let day: string;

  // Prefer pre-formatted strings
  if (monthString && dayString) {
    month = monthString;
    day = dayString;
  } else if (date) {
    // Fallback to auto-formatting from date
    const dateObj = typeof date === 'string' ? new Date(date) : date;

    month = dateObj.toLocaleDateString('en-US', { month: 'short' }).toUpperCase();
    day = dateObj.toLocaleDateString('en-US', { day: 'numeric' });
  } else {
    // No valid input provided
    month = '';
    day = '';
  }

  return (
    <div css={calendarStyles}>
      <div css={[calendarMonthStyles, isPast && disabledCalendarMonthStyles]}>
        <p>{month}</p>
      </div>
      <div css={[calendarDayStyles, isPast && disabledCalendarDayStyles]}>
        <p>{day}</p>
      </div>
    </div>
  );
};

const calendarStyles = css({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  width: '3.5rem',
  padding: '.25rem 0',
  flexShrink: 0,
  borderRadius: '4px',
});

const calendarMonthStyles = css({
  display: 'flex',
  maxHeight: '1rem',
  padding: '1rem',
  paddingBottom: '.75rem',
  justifyContent: 'center',
  alignItems: 'center',
  gap: '1rem',
  alignSelf: 'stretch',
  borderRadius: '4px 4px 0 0',
  backgroundColor: token('color.border.brand'),
  p: {
    color: token('color.text.inverse'),
    textAlign: 'center',
    fontFamily: 'Charlie Display',
    fontSize: '1rem',
    fontStyle: 'normal',
    fontWeight: 600,
    lineHeight: '1.5rem',
  },
});

const calendarDayStyles = css({
  display: 'flex',
  maxHeight: '2rem',
  padding: '1rem',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  gap: '1rem',
  alignSelf: 'stretch',
  borderRadius: '0 0 4px 4px',
  border: `2px solid ${token('color.border.brand')}`,
  backgroundColor: token('color.text.inverse'),
  p: {
    color: token('color.text'),
    textAlign: 'center',
    fontFamily: 'Atlassian Sans',
    fontSize: '1.25rem',
    fontStyle: 'normal',
    fontWeight: 700,
    lineHeight: '2.5rem',
  },
});

const disabledCalendarMonthStyles = css({
  backgroundColor: token('color.icon.disabled'),
});

const disabledCalendarDayStyles = css({
  border: `2px solid ${token('color.icon.disabled')}`,

  p: {
    color: token('color.text.subtlest'),
  },
});
